import React, { FunctionComponent } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Grid,
    TextField,
    Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import styles from '../tcm-referral-request/TcmReferralRequest.Styles';
import { EmptyDataLabel } from '../../../../generic-components/empty-data-label/EmptyDataLabel.Component';
import ServiceTypeRequestRtwEntity from '../../../../../entities/rtw/ServiceTypeRequestRtw.Entity';
import { RTW_HOURS_PER_WEEK_FIELD_LABEL } from '../../../referral-add/referral-service/RtwServiceConstants';
import { ReferralOverviewDataModel } from '../../../../../data-models/ReferralOverview.DataModel';

interface IRtwReferralRequestProps {
    rtwServiceTypeRequest?: ServiceTypeRequestRtwEntity;
    referralOverviewDataModel?: ReferralOverviewDataModel;
}

const boolToYesNo = (value: boolean | undefined): string => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '';
};

const readonlyFieldProps = (classes: ReturnType<typeof styles>) => ({
    className: classes.input,
    disabled: true as const,
    InputProps: { disableUnderline: true },
    InputLabelProps: { shrink: true },
});

export const RtwReferralRequest: FunctionComponent<IRtwReferralRequestProps> = ({
    rtwServiceTypeRequest,
    referralOverviewDataModel,
}) => {
    const classes = styles();

    if (!rtwServiceTypeRequest) {
        return <EmptyDataLabel />;
    }

    const serviceTypeName = referralOverviewDataModel?.referralServiceType
        ?.map((st) => st.serviceTypeName)
        .join(', ');

    const fieldProps = readonlyFieldProps(classes);

    return (
        <form noValidate autoComplete="off">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Accordion className={classes.paper} defaultExpanded>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                            className={classes.paperHeader}
                        >
                            <Typography variant="subtitle2" className={classes.paperTitle}>
                                Return To Work
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={3} className={classes.gridContainer}>
                                {serviceTypeName && (
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Service Type"
                                            {...fieldProps}
                                            value={serviceTypeName}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <TextField
                                        label="This is a Rush Request"
                                        {...fieldProps}
                                        value={boolToYesNo(rtwServiceTypeRequest.rushRequest)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Current Physical Restrictions provided by a treating physician"
                                        maxRows={6}
                                        multiline
                                        {...fieldProps}
                                        value={rtwServiceTypeRequest.physicalRestrictions ?? ''}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Primary Language"
                                        {...fieldProps}
                                        value={rtwServiceTypeRequest.primaryLanguage ?? ''}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Hourly Rate of Pay Employer will Pay while in Return to Work"
                                        {...fieldProps}
                                        value={rtwServiceTypeRequest.hourlyRate ?? ''}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Is the Injured Worker a Union Member?"
                                        {...fieldProps}
                                        value={rtwServiceTypeRequest.isUnionMember ?? ''}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Assignments are typically 32–40 hours per week. For alternate hours, check here."
                                        {...fieldProps}
                                        value={boolToYesNo(rtwServiceTypeRequest.requiresAlternateHoursEnabled)}
                                    />
                                </Grid>
                                {rtwServiceTypeRequest.requiresAlternateHoursEnabled && (
                                    <Grid item xs={12}>
                                        <TextField
                                            label={RTW_HOURS_PER_WEEK_FIELD_LABEL}
                                            {...fieldProps}
                                            value={rtwServiceTypeRequest.transitionHoursPerWeek ?? ''}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <TextField
                                        label="Assignments are typically between 7A-7P, Mon–Sat. For an alternate schedule, check here."
                                        {...fieldProps}
                                        value={boolToYesNo(rtwServiceTypeRequest.requiresAlternateScheduleEnabled)}
                                    />
                                </Grid>
                                {rtwServiceTypeRequest.requiresAlternateScheduleEnabled && (
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Work Schedule Preferences"
                                            maxRows={4}
                                            multiline
                                            {...fieldProps}
                                            value={rtwServiceTypeRequest.workSchedulePreferences ?? ''}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <TextField
                                        label="Which location would you like the Injured worker to work light duty?"
                                        {...fieldProps}
                                        value={rtwServiceTypeRequest.lightDutyLocation ?? ''}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Additional Comments"
                                        maxRows={6}
                                        multiline
                                        {...fieldProps}
                                        value={rtwServiceTypeRequest.additionalInformation ?? ''}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>
        </form>
    );
};
